import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import { useSpring, animated } from 'react-spring';
import Select from 'react-select';
import moment from 'moment';
import { useSelector } from 'react-redux';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import AttachmentButton from './AttachmentButton';

const AnimatedPinSelectBox = styled(animated.div)`
  cursor: ${(props) => (props.isPinClicked ? 'pointer' : 'default')};
  width: 115px;
  color: #444444;
`;

const ButtonCircle = styled('div')`
  display: flex;
  color: #9e9e9e;
  justify-content: center;
  align-items: center;
  border: ${(props) => (props.isClicked ? '1px solid #0dcc8a' : '1px solid #9e9e9e')};
  border-radius: 50%;
  padding: 5px;
  margin-right: 15px;
  cursor: pointer;
  background: ${(props) => (props.isClicked ? '#0dcc8a' : 'none')};
  min-width: 38px;
  min-height: 38px;
  max-height: 38px;
  max-width: 38px;
  @media screen and (max-width: 940px) {
    margin-right: 8px;
  }
`;

const ButtonPinWrapper = styled('div')`
  display: flex;
  align-items: center;
`;

const ButtonWrapper = styled('div')`
  display: flex;
  align-items: center;
`;

const selectBoxCustomStyles = {
  option: (styles) => ({
    ...styles,
    cursor: 'pointer',
  }),
  control: (styles) => ({
    ...styles,
    cursor: 'pointer',
    width: '220px',
  }),
  menu: (styles) => ({
    ...styles,
    width: '200px',
  }),
};

const PostActionButtons = ({
  setFieldValue,
  values,
  editObject = {},
}) => {
  const isEditMode = !!Object.keys(editObject).length;

  const [isPinClicked, setIsPinClicked] = useState(
    isEditMode ? editObject.pinned : !!values.pinValue,
  );
  const [isCommentsDisabledClicked, setIsCommentsDisabledClicked] = useState(
    values.commentsDisabled,
  );

  const pinSelectBox = useSpring({ opacity: isPinClicked ? '1' : '0' });

  const currentUser = useSelector((state) => state.auth.data.currentUser);

  const pinSelectOptions = useMemo(() => {
    const pinOptions = [
      { id: moment().add(1, 'days').unix(), name: '1 Day' },
      { id: moment().add(7, 'days').unix(), name: '1 Week' },
      { id: moment().add(1, 'months').unix(), name: '1 Month' },
      { id: moment().add(3, 'months').unix(), name: '3 Months' },
      { id: moment().add(6, 'months').unix(), name: '6 Months' },
      { id: moment().add(1, 'years').unix(), name: '1 Year' },
    ];
    if (isEditMode && editObject.pinned) {
      const returnOb = {};
      returnOb.id = editObject.pinnedUntil;
      const newMoment = moment.unix(editObject.pinnedUntil);
      const newDate = newMoment.format('MMMM Do, YYYY');
      returnOb.name = newDate;
      pinOptions.unshift(returnOb);
    }
    return pinOptions;
  }, [isEditMode, editObject]);

  const pinSelectValue = useMemo(() => {
    if (values.pinValue && values.pinValue.id) {
      const newMoment = moment.unix(values.pinValue.id);
      const newDate = newMoment.format('MMMM Do, YYYY');
      return { id: values.pinValue.id, name: `${newDate}` };
    }
    return null;
  }, [values.pinValue]);

  const handleCommentsDisabledClick = () => {
    if (isCommentsDisabledClicked) {
      setFieldValue('commentsDisabled', null);
    } else {
      setFieldValue('commentsDisabled', true);
    }
    setIsCommentsDisabledClicked((prevState) => !prevState);
  };

  const handlePinClick = () => {
    setFieldValue('pinValue', isPinClicked ? null : { id: null });
    setIsPinClicked((prevState) => !prevState);
  };

  const handlePinSelectChange = (option) => {
    setFieldValue('pinValue', option);
  };

  const renderActionButtons = () => (
    <ButtonWrapper>
      <AttachmentButton />
      <ButtonCircle
        isClicked={isCommentsDisabledClicked}
        onClick={handleCommentsDisabledClick}
        aria-label={isCommentsDisabledClicked ? 'Enable comments' : 'Disable comments'}
      >
        <IcomoonReact
          iconSet={iconSet}
          size={20}
          icon='comment-disabled'
          color={!isCommentsDisabledClicked ? '#9e9e9e' : '#ffffff'}
        />
      </ButtonCircle>
      {currentUser.admin && (
        <ButtonCircle
          isClicked={isPinClicked}
          onClick={handlePinClick}
          aria-label={isPinClicked ? 'Unpin post' : 'Pin post'}
        >
          <IcomoonReact
            iconSet={iconSet}
            size={20}
            icon='pin'
            color={!isPinClicked ? '#9e9e9e' : '#ffffff'}
          />
        </ButtonCircle>
      )}
    </ButtonWrapper>
  );

  const renderPinSelectBox = () => (
    <AnimatedPinSelectBox style={pinSelectBox} isPinClicked={isPinClicked}>
      <Select
        value={pinSelectValue}
        getOptionLabel={(option) => option.name}
        getOptionValue={(option) => option.id}
        options={pinSelectOptions}
        onChange={handlePinSelectChange}
        styles={selectBoxCustomStyles}
        isDisabled={!isPinClicked}
        placeholder='Pin for...'
        aria-label='Select pin duration'
      />
    </AnimatedPinSelectBox>
  );

  return (
    <ButtonPinWrapper isPinClicked={isPinClicked}>
      {renderActionButtons()}
      {renderPinSelectBox()}
    </ButtonPinWrapper>
  );
};

PostActionButtons.propTypes = {
  values: PropTypes.instanceOf(Object).isRequired,
  setFieldValue: PropTypes.func.isRequired,
  editObject: PropTypes.instanceOf(Object),
};

PostActionButtons.defaultProps = {
  editObject: {},
};

export default PostActionButtons;
